import { useEffect, useState } from 'react';
import { Conversation_Kind } from '@gen/chat/v1/common_pb';
import { useStrings } from '@/i18n/Localization';
import { AttachmentView } from '@/components/AttachmentView';
import { MessageMenu } from '@/components/MessageMenu';
import { PollCard } from '@/components/Poll';
import { ReactionBar } from '@/components/ReactionBar';
import { SendIndicator, sendStateFor } from '@/components/SendIndicator';
import { formatApproximateCount } from '@/media/format';
import type { Hex } from '@/store/ids';
import type {
  ConversationRecord,
  MessageRecord,
  OutboxRecord,
  PollRecord,
  ReactionRecord,
} from '@/store/types';
import type { WorkerClient } from '@/transport/workerClient';
import panels from '@/components/panels.module.css';
import styles from '@/components/MessageRow.module.css';

export interface MessageRowProps {
  message: MessageRecord;
  outbox: OutboxRecord | undefined;
  conversation: ConversationRecord | null;
  worker: WorkerClient | null;
  ownUserId: string;
  /** True when this continues a run from the same sender within two minutes. */
  grouped: boolean;
  /**
   * The day label to show above this message, when it opens a new day.
   *
   * Rendered inside the row rather than as its own list entry because the
   * timeline is virtualised: a separate separator row would need its own index in
   * the virtual model, and every message index would shift. The row is measured,
   * so the extra height is handled already.
   */
  dayLabel?: string | null;
  senderName: string;
  reactions: readonly ReactionRecord[];
  poll: PollRecord | null;
  canPin: boolean;
  canDeleteOthers: boolean;
  autoPlayVoice: boolean;
  onReply: (message: MessageRecord) => void;
  onEdit: (message: MessageRecord) => void;
  onDelete: (message: MessageRecord) => void;
  onForward: (message: MessageRecord) => void;
  onPin: (message: MessageRecord) => void;
  onReact: (message: MessageRecord, reaction: string, active: boolean) => void;
  onVote: (poll: PollRecord, optionIds: Hex[]) => void;
  onClosePoll: (poll: PollRecord) => void;
  onVoicePosition: (message: MessageRecord, positionMs: number) => void;
  onSeen: (message: MessageRecord) => void;
  onRetry: (opId: string) => void;
  onDiscard: (opId: string) => void;
}

/**
 * One message.
 *
 * Content is rendered as text nodes. React escapes those, and nothing in this
 * file uses `dangerouslySetInnerHTML` — Section 05 requires escaping content and
 * never rendering arbitrary user HTML, and the way to guarantee that is for the
 * unsafe API to be absent rather than guarded.
 *
 * `dir="auto"` on the text is what supports mixed-direction content from Phase
 * 1: the first strong directional character decides the paragraph direction, so
 * an Arabic message inside an English interface reads correctly without the
 * sender having to mark it up and without the interface guessing from a locale
 * setting that describes the reader rather than the text.
 *
 * View counts appear only on channel posts and only as an approximation.
 * Section 06 is explicit: "Do not present views as delivery/read receipts," so
 * the label says "about N views" and the send indicators — which *are* receipts
 * — are a different control in a different place.
 */
export function MessageRow(props: MessageRowProps) {
  const { message, outbox, conversation, grouped, senderName } = props;
  const { t, formatTime } = useStrings();
  const [menuOpen, setMenuOpen] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);

  const state = message.outgoing ? sendStateFor(message, outbox, conversation) : null;
  const failedOpId = state === 'failed' ? message.opId ?? outbox?.opId ?? null : null;
  const protectedContent = conversation?.protectedContent ?? false;
  // View counts exist only for channel posts; Section 06 defines them nowhere
  // else, and showing a count on a direct message would invent a receipt.
  const isChannel = conversation?.kind === Conversation_Kind.CHANNEL;

  /**
   * Which delivery tick this message has earned, or null for none.
   *
   * Own committed messages only. An incoming message has no ticks to show, and a
   * message still in the outbox is covered by the send indicator beside it —
   * showing a tick for something that has not committed would claim it was sent.
   *
   * Channels are excluded: a post has no single recipient whose read position
   * could mean anything, and the view count is the honest figure there.
   */
  const deliveryState: 'sent' | 'delivered' | 'read' | null = (() => {
    if (!message.outgoing || message.deleted) return null;
    if (message.localState !== 'COMMITTED' || message.createdSeq <= 0) return null;
    if (isChannel || conversation === null) return null;

    if (conversation.peerReadSeq >= message.createdSeq) return 'read';
    if (conversation.peerDeliveredSeq >= message.createdSeq) return 'delivered';
    return 'sent';
  })();

  // A channel post that has been rendered counts as viewed. Section 06
  // deduplicates by account, post and day; the caller holds that guard so this
  // fires at most once per post per day even as rows scroll in and out.
  useEffect(() => {
    if (isChannel && !message.outgoing && message.createdSeq > 0) {
      props.onSeen(message);
    }
    // Deliberately keyed on identity alone: a re-render for an unrelated reason
    // must not report a second view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.id, isChannel]);

  const copy = () => {
    setMenuOpen(false);
    void navigator.clipboard.writeText(message.content.text).catch(() => {
      // Clipboard access is refused in some contexts and cannot be worked
      // around; saying so is better than a control that appears to do nothing.
      setCopyFailed(true);
    });
  };

  return (
    // A plain element, not a list item: the virtualizer needs one measured
    // wrapper per row and that wrapper is the <li> in Timeline, so nesting a
    // second list item here would produce `ol > div > li` and break the list
    // semantics a screen reader relies on to announce position.
    <div
      className={`${styles.row} ${message.outgoing ? styles.outgoing : styles.incoming} ${grouped ? styles.grouped : ''} ${props.dayLabel ? styles.withDay : ''}`}
    >
      {/*
        The day chip, when this message opens a new day. It is centred across the
        whole row rather than placed in the bubble column, so it does not inherit
        the row's outgoing or incoming alignment.
      */}
      {props.dayLabel ? <div className={styles.dayChip}>{props.dayLabel}</div> : null}
      <div className={styles.bubbleWrapper}>
        {!grouped && !message.outgoing ? (
          <div className={styles.sender} dir="auto">
            {senderName}
          </div>
        ) : null}

        <div className={styles.bubble}>
          {message.forwardOrigin !== null ? (
            <div className={styles.sender} dir="auto">
              {message.forwardOrigin.displayName.length > 0
                ? t('forward.origin', { name: message.forwardOrigin.displayName })
                : // Section 06: where the origin does not permit attribution,
                  // the copy is shown without leaking origin identifiers.
                  t('forward.originHidden')}
            </div>
          ) : null}

          {message.deleted ? (
            <p className={styles.deleted}>{t('message.deleted')}</p>
          ) : (
            <>
              {props.poll === null ? null : (
                <PollCard
                  poll={props.poll}
                  canClose={message.outgoing || props.canDeleteOthers}
                  onVote={(optionIds) => {
                    if (props.poll) {
                      props.onVote(props.poll, optionIds);
                    }
                  }}
                  onClose={() => {
                    if (props.poll) {
                      props.onClosePoll(props.poll);
                    }
                  }}
                />
              )}

              {message.content.attachments.length > 0 ? (
                <AttachmentView
                  worker={props.worker}
                  conversationId={message.conversationId}
                  messageId={message.id}
                  attachments={message.content.attachments}
                  kind={message.content.kind}
                  protectedContent={protectedContent}
                  autoPlayVoice={props.autoPlayVoice}
                  onVoicePosition={(positionMs) => {
                    props.onVoicePosition(message, positionMs);
                  }}
                />
              ) : null}

              {message.content.text.length > 0 ? (
                <p className={styles.text} dir="auto">
                  {message.content.text}
                </p>
              ) : null}
            </>
          )}

          <div className={styles.meta}>
            <time
              className={styles.time}
              dateTime={new Date(message.createdAt).toISOString()}
              // The visible time is local; the machine-readable value above is
              // absolute, so assistive technology and the reader agree.
            >
              {formatTime(message.createdAt)}
            </time>
            {message.pinned ? <span className={styles.edited}>{t('pin.action')}</span> : null}
            {message.editedAt !== null && !message.deleted ? (
              <span className={styles.edited}>{t('message.edited')}</span>
            ) : null}
            {isChannel && message.viewCount > 0 ? (
              <span className={styles.edited} title={t('channel.viewsHint')}>
                {t('channel.views', { count: formatApproximateCount(message.viewCount) })}
              </span>
            ) : null}
            {state ? <SendIndicator state={state} /> : null}
            {/*
              Delivery ticks, on this account's own committed messages only.

              The positions come from READ_CHANGED events the server already
              fans out, which the store has been recording as peerReadSeq and
              peerDeliveredSeq all along — the data was plumbed end to end and
              only this glyph was missing.

              Three states, and the distinction matters: one tick means the
              server committed it, two means the recipient's device stored it,
              and two filled means a person opened the conversation. Showing
              "read" for a delivery would claim somebody saw a message they have
              not.
            */}
            {deliveryState !== null ? (
              <span
                className={`${styles.ticks} ${deliveryState === 'read' ? styles.ticksRead : ''}`}
                title={t(`message.${deliveryState}`)}
                aria-label={t(`message.${deliveryState}`)}
              >
                <span aria-hidden="true">{deliveryState === 'sent' ? '✓' : '✓✓'}</span>
              </span>
            ) : null}
          </div>
        </div>

        {!message.deleted && message.localState === 'COMMITTED' ? (
          <ReactionBar
            reactions={props.reactions}
            ownUserId={props.ownUserId}
            disabled={false}
            onToggle={(reaction, active) => {
              props.onReact(message, reaction, active);
            }}
          />
        ) : null}

        {failedOpId ? (
          <div className={styles.failure} role="status">
            <span className={styles.failureText}>{outbox?.lastError?.message ?? t('status.failed')}</span>
            <button
              type="button"
              className={styles.failureAction}
              onClick={() => {
                props.onRetry(failedOpId);
              }}
            >
              {t('message.retry')}
            </button>
            <button
              type="button"
              className={styles.failureAction}
              onClick={() => {
                props.onDiscard(failedOpId);
              }}
            >
              {t('message.discard')}
            </button>
          </div>
        ) : null}

        {copyFailed ? (
          <p className={styles.failureText} role="status">
            {t('message.copyFailed')}
          </p>
        ) : null}

        {protectedContent ? (
          <p className={panels.hint}>{t('media.protected')}</p>
        ) : null}

        <div className={styles.actions}>
          <button
            type="button"
            className={styles.actionsButton}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t('message.actions')}
            onClick={() => {
              setMenuOpen((open) => !open);
            }}
          >
            <span aria-hidden="true">⋯</span>
          </button>
          {menuOpen ? (
            <MessageMenu
              // Editing is offered only for this account's own messages that
              // actually exist on the server. A pending message has no version
              // to edit against, and an edit queued behind an unsent send would
              // reference a message identifier the server has never issued.
              canEdit={message.outgoing && !message.deleted && message.localState === 'COMMITTED'}
              // Section 06 lets a group or channel administrator delete any
              // message, while an ordinary member may only delete their own.
              canDelete={
                !message.deleted &&
                message.localState === 'COMMITTED' &&
                (message.outgoing || props.canDeleteOthers)
              }
              canCopy={!protectedContent && !message.deleted && message.content.text.length > 0}
              canForward={
                !protectedContent && !message.deleted && message.localState === 'COMMITTED'
              }
              canPin={props.canPin && !message.deleted && message.localState === 'COMMITTED'}
              pinned={message.pinned}
              onReply={() => {
                setMenuOpen(false);
                props.onReply(message);
              }}
              onCopy={copy}
              onEdit={() => {
                setMenuOpen(false);
                props.onEdit(message);
              }}
              onDelete={() => {
                setMenuOpen(false);
                props.onDelete(message);
              }}
              onForward={() => {
                setMenuOpen(false);
                props.onForward(message);
              }}
              onPin={() => {
                setMenuOpen(false);
                props.onPin(message);
              }}
              onClose={() => {
                setMenuOpen(false);
              }}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}